'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useCart } from '@/app/context/CartContext';
import { useAuth } from '@/app/context/AuthContext';
import { useToast } from '@/app/context/ToastContext';
import { ShoppingCart, Trash2, Shield, Battery, MessageCircle } from 'lucide-react';
import { getConditionBadgeLabel } from '@/lib/constants/products';

const getConditionColor = (condition) => {
  const colors = {
    'NEW': 'bg-blue-100 text-blue-700',
    'REFURBISHED_EXCELLENT': 'bg-green-100 text-green-700',
    'REFURBISHED': 'bg-emerald-100 text-emerald-700',
    'REFURBISHED_GOOD': 'bg-amber-100 text-amber-700',
  };
  return colors[condition] || 'bg-gray-100 text-gray-700';
};

export default function ProductCard({ product }) {
  const router = useRouter();
  const { user } = useAuth();
  const { addToCart, removeFromCart, cartItems } = useCart();
  const { showToast } = useToast();
  const imageUrl = product.images ? JSON.parse(product.images)[0] : null;
  const isSparePart = product.category === 'SPARE_PARTS';

  const cartItem = cartItems.find(item => item.product?.id === product.id || item.productId === product.id);
  const isInCart = !!cartItem;

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      router.push('/login');
      return;
    }

    const result = await addToCart(product);
    if (result && result.success) {
      showToast('Produit ajouté au panier', 'success');
    } else {
      showToast(result?.error || 'Erreur lors de l\'ajout au panier', 'error');
    }
  };

  const handleRemoveFromCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (cartItem) {
      await removeFromCart(cartItem.id);
      showToast('Produit retiré du panier', 'remove');
    }
  };

  return (
    <Link href={`/products/${product.slug}`} className="block h-full">
      <div className="bg-white rounded-lg sm:rounded-xl shadow-sm hover:shadow-md border border-gray-100 hover:border-[#23994F]/30 transition-all duration-300 overflow-hidden flex flex-col group h-full">

        <div className="relative bg-gradient-to-br from-[#23994F]/5 to-gray-50 h-40 sm:h-52 lg:h-56 flex items-center justify-center overflow-hidden">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={product.name}
              className="w-auto h-36 sm:h-48 lg:h-52 object-contain group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
              <span className="text-gray-400 text-xs">Image non disponible</span>
            </div>
          )}


          {product.condition && (
            <div className="absolute top-2 right-2">
              <span className={`px-2 py-0.5 text-[10px] sm:text-xs font-semibold rounded whitespace-nowrap ${getConditionColor(product.condition)}`}>
                {getConditionBadgeLabel(product.condition)}
              </span>
            </div>
          )}
        </div>


        <div className="p-3 sm:p-4 flex-grow flex flex-col">
          <div className="flex-grow">
            <p className="text-[10px] sm:text-xs text-gray-500 uppercase font-semibold mb-0.5">{product.brand}</p>
            <h3 className="font-bold text-sm sm:text-base text-gray-900 leading-tight mb-2 line-clamp-2 group-hover:text-[#23994F] transition-colors">
              {product.name}
            </h3>


            <div className="flex flex-wrap gap-2 mb-2 text-[10px] sm:text-xs text-gray-600">
              {product.storage && (
                <span className="font-medium">{product.storage}</span>
              )}
              {product.batteryHealth && (
                <span className="flex items-center gap-1">
                  <Battery size={12} />
                  <span>{product.batteryHealth}%</span>
                </span>
              )}
              {product.warranty && (
                <span className="flex items-center gap-1">
                  <Shield size={12} />
                  <span>{product.warranty} mois</span>
                </span>
              )}
            </div>


            {!isSparePart && (
              <div className="flex items-baseline gap-2 mb-3">
                <span className="text-lg sm:text-xl lg:text-2xl font-bold text-[#23994F]">
                  {parseFloat(product.price).toFixed(2)}€
                </span>
                {product.originalPrice && (
                  <span className="text-xs sm:text-sm text-gray-400 line-through font-medium">
                    {parseFloat(product.originalPrice).toFixed(2)}€
                  </span>
                )}
              </div>
            )}
            {isSparePart && (
              <div className="mb-3">
                <span className="text-base sm:text-lg font-semibold text-gray-600">
                  Prix sur devis
                </span>
              </div>
            )}
          </div>


          <div className="flex gap-2 mt-auto">
            {isSparePart ? (
              <a
                href="https://wa.me/33651655554"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 sm:py-2.5 bg-green-500 text-white font-semibold text-xs sm:text-sm rounded-lg hover:bg-green-600 transition-colors"
              >
                <MessageCircle size={16} />
                <span className="hidden xs:inline">WhatsApp</span>
              </a>
            ) : (
              <>
                {isInCart ? (
                  <button
                    onClick={handleRemoveFromCart}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 sm:py-2.5 bg-red-500 text-white font-semibold text-xs sm:text-sm rounded-lg hover:bg-red-600 transition-colors"
                  >
                    <Trash2 size={16} />
                    <span className="hidden xs:inline">Retirer</span>
                  </button>
                ) : (
                  <button
                    onClick={handleAddToCart}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 sm:py-2.5 bg-[#23994F] text-white font-semibold text-xs sm:text-sm rounded-lg hover:bg-[#1a7840] transition-colors"
                  >
                    <ShoppingCart size={16} />
                    <span className="hidden xs:inline">Ajouter</span>
                  </button>
                )}
              </>
            )}
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                router.push(`/products/${product.slug}`);
              }}
              className="px-3 sm:px-4 py-2 sm:py-2.5 bg-gray-100 text-gray-800 font-semibold text-xs sm:text-sm rounded-lg hover:bg-gray-200 transition-colors"
            >
              <span className="hidden sm:inline">Détails</span>
              <span className="sm:hidden">→</span>
            </button>
          </div>
        </div>
      </div>
    </Link>
  );
}
